import { api } from "../api";
import { el, clear } from "../dom";
import { addDays, formatDayHead, windowLabel } from "../../shared/present";
import { bookingUrl, type LunacalConfig } from "../../shared/lunacal";
import { zonedParts } from "../../shared/slots";

const PROFILE = "https://lunacal.ai/letsgo";

interface SlotsRes {
  slots: number[];
  slotLenMin: number;
  bookingBase: string;
  fetchedAt: string;
  stale: boolean;
}

function asSlots(v: unknown): SlotsRes | null {
  if (typeof v !== "object" || v === null) return null;
  const rec = v as Record<string, unknown>;
  if (!Array.isArray(rec.slots)) return null;
  return {
    slots: rec.slots.filter((n): n is number => typeof n === "number"),
    slotLenMin: typeof rec.slotLenMin === "number" ? rec.slotLenMin : 15,
    bookingBase: typeof rec.bookingBase === "string" && rec.bookingBase ? rec.bookingBase : PROFILE,
    fetchedAt: typeof rec.fetchedAt === "string" ? rec.fetchedAt : "",
    stale: rec.stale === true,
  };
}

export async function renderBook(root: HTMLElement): Promise<{ destroy(): void }> {
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const wrap = el("div", { class: "wrap" });
  const panel = el("div", { class: "panel" });
  panel.append(el("h1", { text: "Book a 1:1" }));
  const zoneLine = el("p");
  zoneLine.append(el("span", { class: "label", text: "Timezone" }), document.createTextNode(` ${viewerTz}`));
  const staleLine = el("p", { class: "muted" });
  const errLine = el("p", { class: "error" });
  const retry = el("button", { type: "button", text: "Try again" });
  retry.hidden = true;
  const daysEl = el("div", { class: "day-strip", "data-testid": "book-days" });
  const timesEl = el("div", { class: "time-list", "data-testid": "book-times" });
  const confirmEl = el("div", { class: "confirm-row" });
  const fallback = el("p", { "data-testid": "book-fallback" });
  panel.append(zoneLine, staleLine, errLine, retry, daysEl, timesEl, confirmEl, fallback);
  wrap.append(panel);
  root.replaceChildren(wrap);

  const today = zonedParts(Math.floor(Date.now() / 60_000), viewerTz).date;
  const days: string[] = [];
  for (let i = 0; i < 21; i++) days.push(addDays(today, i));

  let slots: number[] = [];
  let bookingBase = PROFILE;
  let slotLenMin = 15;
  let selectedDay: string | null = null;
  let selectedSlot: number | null = null;
  let grouped = new Map<string, number[]>();

  function groupOnce(list: number[]): Map<string, number[]> {
    const map = new Map<string, number[]>();
    for (const s of list) {
      const d = zonedParts(s, viewerTz).date;
      const arr = map.get(d) ?? [];
      arr.push(s);
      map.set(d, arr);
    }
    return map;
  }

  function cfg(): LunacalConfig {
    try {
      const u = new URL(bookingBase);
      if (u.protocol !== "https:") throw new Error("not https");
      const parts = u.pathname.split("/").filter(Boolean);
      return {
        base: u.origin,
        user: parts[0] ?? "letsgo",
        event: parts[1] ?? "meeting",
        duration: String(slotLenMin),
      };
    } catch {
      return { base: "https://lunacal.ai", user: "letsgo", event: "meeting", duration: String(slotLenMin) };
    }
  }

  function profileUrl(): string {
    try {
      const u = new URL(bookingBase);
      if (u.protocol !== "https:") return PROFILE;
      const parts = u.pathname.split("/").filter(Boolean);
      u.pathname = `/${parts[0] ?? "letsgo"}`;
      u.search = "";
      return u.toString().replace(/\/$/, "");
    } catch {
      return PROFILE;
    }
  }

  function showFallback(href: string, extra = ""): void {
    clear(fallback);
    if (extra) fallback.append(document.createTextNode(`${extra} `));
    else fallback.append(document.createTextNode("Openings are not listed here. "));
    fallback.append(el("a", { href, text: "Book on Lunacal" }));
  }

  function renderDays(): void {
    clear(daysEl);
    if (!slots.length) return;
    for (const d of days) {
      const open = grouped.get(d) ?? [];
      const b = el("button", {
        type: "button",
        class: selectedDay === d ? "primary" : "",
        text: formatDayHead(d),
      });
      if (!open.length) b.disabled = true;
      else {
        b.addEventListener("click", () => {
          selectedDay = d;
          selectedSlot = null;
          renderDays();
          renderTimes();
          renderConfirm();
        });
      }
      daysEl.append(b);
    }
  }

  function renderTimes(): void {
    clear(timesEl);
    if (!selectedDay) return;
    const list = grouped.get(selectedDay) ?? [];
    if (!list.length) {
      timesEl.append(el("p", { class: "muted", text: "No openings this day." }));
      return;
    }
    for (const s of list) {
      const b = el("button", {
        type: "button",
        class: selectedSlot === s ? "active" : "",
        text: zonedParts(s, viewerTz).label,
      });
      b.addEventListener("click", () => {
        selectedSlot = s;
        renderDays();
        renderTimes();
        renderConfirm();
      });
      timesEl.append(b);
    }
  }

  function renderConfirm(): void {
    clear(confirmEl);
    if (selectedSlot === null) return;
    const go = el("button", {
      class: "primary",
      type: "button",
      text: "Continue to confirm",
      "data-testid": "book-continue",
    });
    const msg = el("p", {
      class: "muted",
      "data-testid": "book-message",
      text: "You will confirm on Lunacal.",
    });
    go.addEventListener("click", () => {
      void (async () => {
        if (go.disabled) return;
        go.disabled = true;
        try {
          const fresh = await load();
          if (!fresh) {
            msg.textContent = errLine.textContent || "That time was just taken. Pick another.";
            selectedSlot = null;
            renderDays();
            renderTimes();
            renderConfirm();
            confirmEl.append(msg);
            return;
          }
          if (selectedSlot === null || !slots.includes(selectedSlot)) {
            msg.textContent = "That time was just taken. Pick another.";
            selectedSlot = null;
            renderDays();
            renderTimes();
            renderConfirm();
            confirmEl.append(msg);
            return;
          }
          const url = bookingUrl(cfg(), selectedSlot, viewerTz);
          if (!url.startsWith("https://")) return;
          location.assign(url);
        } catch (e) {
          console.error(e);
          msg.textContent = "Could not reach the server.";
        } finally {
          go.disabled = false;
        }
      })();
    });
    confirmEl.append(
      el("p", null, el("strong", { text: windowLabel(selectedSlot, selectedSlot + slotLenMin, viewerTz) })),
      el("p", null, go),
      msg,
    );
  }

  async function load(): Promise<boolean> {
    errLine.textContent = "";
    retry.hidden = true;
    const res = await api("GET", "/api/lunacal/slots", { parse: asSlots });
    if (!res.ok) {
      errLine.textContent = res.error;
      retry.hidden = false;
      selectedSlot = null;
      slots = [];
      grouped = new Map();
      clear(daysEl);
      clear(timesEl);
      clear(confirmEl);
      showFallback(PROFILE);
      return false;
    }
    slots = res.data.slots;
    bookingBase = res.data.bookingBase.startsWith("https:") ? res.data.bookingBase : PROFILE;
    slotLenMin = res.data.slotLenMin;
    grouped = groupOnce(slots);
    staleLine.textContent = res.data.stale ? "These times may be a few minutes old." : "";
    if (!slots.length) {
      showFallback(profileUrl());
      selectedDay = null;
      selectedSlot = null;
      renderDays();
      clear(timesEl);
      clear(confirmEl);
      return false;
    }
    const inStrip = days.some((d) => (grouped.get(d) ?? []).length > 0);
    if (!inStrip) {
      const first = slots[0];
      const date = first !== undefined ? zonedParts(first, viewerTz).date : "";
      showFallback(profileUrl(), date ? `The first opening is ${formatDayHead(date)}.` : "");
      selectedDay = null;
      selectedSlot = null;
      renderDays();
      clear(timesEl);
      clear(confirmEl);
      return false;
    }
    clear(fallback);
    if (!selectedDay || !(grouped.get(selectedDay) ?? []).length) {
      selectedDay = days.find((d) => (grouped.get(d) ?? []).length) ?? days[0] ?? null;
    }
    renderDays();
    renderTimes();
    renderConfirm();
    return true;
  }

  retry.addEventListener("click", () => {
    void load().catch((e) => {
      console.error(e);
      errLine.textContent = "Could not reach the server.";
    });
  });

  await load();
  return { destroy() {} };
}
